// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React from 'react';
import type {
  PensionRecommendationSectionContent,
  RecommendationOptionSummary,
} from '../../../types/proposal';
import { formatPlanCurrency, formatPercent, formatYearSchedule } from './planFormat';

interface Props {
  readonly content: PensionRecommendationSectionContent;
}

const CHARGE_ROWS: Array<{
  label: string;
  render: (option: RecommendationOptionSummary) => string;
}> = [
  {
    label: 'Net Allocation',
    render: (o) =>
      o.figures?.allocationPercent != null ? formatPercent(o.figures.allocationPercent) : '—',
  },
  {
    label: 'AMC (p.a.)',
    render: (o) => (o.figures?.amcPercent != null ? formatPercent(o.figures.amcPercent) : '—'),
  },
  {
    label: 'Fund Management Charge schedule',
    render: (o) => formatYearSchedule(o.figures?.fundManagementCharges),
  },
  {
    label: 'Exit Penalties',
    render: (o) => formatYearSchedule(o.figures?.exitPenalties),
  },
  {
    label: 'Clawback',
    render: (o) => formatYearSchedule(o.figures?.clawbacks),
  },
];

/**
 * PENSION_RECOMMENDATION — recommendation card (provider, recommended
 * amount/fund, rationale) + the selected option's charges list, and — when
 * the advisor left the comparison visible — a charges comparison table
 * against the pinned alternative quote options.
 */
const PlanRecommendationSection: React.FC<Props> = ({ content }) => {
  const selected = content.selected;
  const comparison = content.comparison ?? [];
  const showComparison = content.comparisonVisible !== false && comparison.length > 0;
  const currency = selected?.figures?.currencyCode;

  return (
    <div data-testid="plan-recommendation">
      {selected && (
        <div className="proposal-plan__provider" data-testid="plan-recommendation-provider">
          {selected.providerLogoUrl && (
            <img className="proposal-plan__provider-logo" src={selected.providerLogoUrl} alt="" />
          )}
          <span className="proposal-plan__provider-name">{selected.providerName}</span>
          <span className="proposal-plan__recommended-chip">Recommended</span>
        </div>
      )}

      <dl className="proposal-plan__facts-list">
        {content.recommendedAmount != null && (
          <div className="proposal-plan__facts-row">
            <dt className="proposal-plan__facts-label">Recommended amount</dt>
            <dd className="proposal-plan__facts-value" data-testid="plan-recommended-amount">
              {formatPlanCurrency(content.recommendedAmount, currency)}
            </dd>
          </div>
        )}
        {content.recommendedFund && (
          <div className="proposal-plan__facts-row">
            <dt className="proposal-plan__facts-label">Recommended fund</dt>
            <dd className="proposal-plan__facts-value" data-testid="plan-recommended-fund">
              {content.recommendedFund}
            </dd>
          </div>
        )}
        {selected?.figures?.amount != null && (
          <div className="proposal-plan__facts-row">
            <dt className="proposal-plan__facts-label">Amount</dt>
            <dd className="proposal-plan__facts-value">
              {formatPlanCurrency(selected.figures.amount, currency)}
            </dd>
          </div>
        )}
      </dl>

      {content.rationale && (
        <div data-testid="plan-recommendation-rationale">
          {content.rationale
            .split(/\n{2,}|\n/)
            .filter(Boolean)
            .map((para, i) => (
              <p className="proposal-plan__narrative" key={i}>
                {para}
              </p>
            ))}
        </div>
      )}

      {selected && (
        <dl className="proposal-plan__facts-list" data-testid="plan-recommendation-charges">
          {CHARGE_ROWS.map(({ label, render }) => (
            <div className="proposal-plan__facts-row" key={label}>
              <dt className="proposal-plan__facts-label">{label}</dt>
              <dd className="proposal-plan__facts-value">{render(selected)}</dd>
            </div>
          ))}
        </dl>
      )}

      {showComparison && selected && (
        <div className="proposal-plan__table-wrap">
          <table className="proposal-plan__table" data-testid="plan-comparison-table">
            <thead>
              <tr>
                <th>Charge</th>
                <th>
                  {selected.providerName}{' '}
                  <span className="proposal-plan__muted">(recommended)</span>
                </th>
                {comparison.map((option, i) => (
                  <th key={`${option.providerName}-${i}`}>{option.providerName}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CHARGE_ROWS.map(({ label, render }) => (
                <tr key={label}>
                  <td>{label}</td>
                  <td>{render(selected)}</td>
                  {comparison.map((option, i) => (
                    <td key={`${option.providerName}-${i}`}>{render(option)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PlanRecommendationSection;
